/**
 * NO04 - Ordre Search Component
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Paper,
    TextField,
    Button,
    Collapse,
    Typography,
    InputAdornment,
    IconButton,
    Popover,
    Alert,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore, ExpandLess, InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import * as actionsNO04 from '../../../store/actions/indexNO04';
import { NO04SearchParams } from '../../../models/NO04';

const useStyles = makeStyles()((theme) => ({
    searchContainer: {
        padding: theme.spacing(2, 3),
        margin: 0,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        flexShrink: 0,
    },
    quickSearchWrapper: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'center',
        maxWidth: '680px',
    },
    quickSearchField: {
        flex: 1,
        backgroundColor: '#f5f5f5',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f5f5',
            height: '42px',
            fontSize: '0.95rem',
        },
        '& .MuiInputBase-input': {
            padding: '8px 12px',
            fontSize: '0.95rem',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000',
        },
    },
    searchButton: {
        minWidth: '80px',
        height: '42px',
        textTransform: 'none',
        fontSize: '0.95rem',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    advancedToggle: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.88rem',
        marginTop: theme.spacing(1),
        '&:hover': { color: theme.palette.primary.main },
    },
    advancedSection: {
        backgroundColor: '#f9f9f9',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(1),
    },
    dateField: {
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root': {
            height: '42px',
            fontSize: '0.95rem',
        },
        '& .MuiInputBase-input': {
            padding: '8px 12px',
            fontSize: '0.95rem',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000',
        },
    },
}));

const getAdvancedInitial = () => ({
    customerNumber: '',
    orderNumber: '',
    requisitionNumber: '',
    invoiceCreditNumber: '',
    fromDate: '',
    toDate: '',
});

export default function NO04Search() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const loading = useSelector((state: AppState) => state.NO04Ordre.loading);
    const companyId = useSelector((state: AppState) => state.auth.userJWT?.Department ?? '');

    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);
    const [quickSearch, setQuickSearch] = useState('');
    const [advancedState, setAdvancedState] = useState(getAdvancedInitial);
    const [validationError, setValidationError] = useState('');

    const handleAdvancedChange = (field: string, value: string) => {
        setAdvancedState((prev) => ({ ...prev, [field]: value }));
    };

    const buildParams = (): NO04SearchParams => {
        const base: NO04SearchParams = { companyId };
        if (!advancedOpen) {
            const v = quickSearch.trim();
            if (!v) return base;
            if (/^\d+$/.test(v)) {
                // Pure number — send as both OrderNumber and CustomerNumber, backend uses OR
                base.orderNumber    = v;
                base.customerNumber = v;
                base.matchAnyNumber = true;
            } else {
                // Alphanumeric — requisition number or invoice credit number
                base.requisitionNumber = v;
            }
            return base;
        }
        if (advancedState.customerNumber.trim())      base.customerNumber      = advancedState.customerNumber.trim();
        if (advancedState.orderNumber.trim())         base.orderNumber         = advancedState.orderNumber.trim();
        if (advancedState.requisitionNumber.trim())   base.requisitionNumber   = advancedState.requisitionNumber.trim();
        if (advancedState.invoiceCreditNumber.trim()) base.invoiceCreditNumber = advancedState.invoiceCreditNumber.trim();
        if (advancedState.fromDate)                   base.fromDate            = advancedState.fromDate;
        if (advancedState.toDate)                     base.toDate              = advancedState.toDate;
        return base;
    };

    const hasSearchParams = (): boolean => {
        if (!advancedOpen) return quickSearch.trim().length > 0;
        return !!(
            advancedState.customerNumber.trim() ||
            advancedState.orderNumber.trim() ||
            advancedState.requisitionNumber.trim() ||
            advancedState.invoiceCreditNumber.trim() ||
            advancedState.fromDate ||
            advancedState.toDate
        );
    };

    const handleSearch = () => {
        if (!hasSearchParams()) {
            setValidationError('Du må fylle inn minst ett søkefelt før du søker.');
            return;
        }
        setValidationError('');
        dispatch(actionsNO04.NO04SearchRequest(buildParams()));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') handleSearch();
    };

    const handleClear = () => {
        setQuickSearch('');
        setAdvancedState(getAdvancedInitial());
        setValidationError('');
        dispatch(actionsNO04.NO04ClearResults());
    };

    const advancedField = (label: string, field: string, placeholder: string) => (
        <Box>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.82rem' }}>
                {label}
            </Typography>
            <TextField
                fullWidth
                size="small"
                placeholder={placeholder}
                value={(advancedState as any)[field]}
                onChange={(e) => handleAdvancedChange(field, e.target.value)}
                onKeyDown={handleKeyDown}
                className={classes.quickSearchField}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );

    return (
        <Paper className={classes.searchContainer} elevation={2}>
            {/* Title row */}
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.25}>
                <Box>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                        Mine ordre
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Søk på ordre siste 5 år
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={(e) => setInfoAnchor(e.currentTarget)}
                    sx={{ color: 'primary.main', mt: -0.5 }}
                >
                    <InfoOutlined fontSize="small" />
                </IconButton>
            </Box>
            <Popover
                open={Boolean(infoAnchor)}
                anchorEl={infoAnchor}
                onClose={() => setInfoAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, maxWidth: 300 }}>
                    <Typography variant="body2">
                        Søk etter ordre på tvers av åpne og fakturerte ordre. Bruk hurtigsøk for raskt
                        oppslag, eller avansert søk for å filtrere på kunde, ordre/faktura,
                        rekvisjonsnummer, beløp og datoperiode.
                    </Typography>
                </Box>
            </Popover>
            {/* Validation error */}
            {validationError && (
                        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.9rem' }} onClose={() => setValidationError('')}>
                    {validationError}
                </Alert>
            )}
            {/* Quick Search (hidden when advanced is open) */}
            {!advancedOpen && (
                <Box className={classes.quickSearchWrapper}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Søk på ordrenr, kundenr eller rekv.nr"
                        value={quickSearch}
                        onChange={(e) => setQuickSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={classes.quickSearchField}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon fontSize="small" />}
                        onClick={handleSearch}
                        disabled={loading}
                        className={classes.searchButton}
                        sx={{ backgroundColor: '#d30535', '&:hover': { backgroundColor: '#b0022c' } }}
                    >
                        Søk
                    </Button>
                </Box>
            )}

            {/* Advanced Toggle */}
            <Box className={classes.advancedToggle} onClick={() => setAdvancedOpen(!advancedOpen)}>
                {advancedOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {advancedOpen ? 'Tilbake til hurtigsøk' : 'Avansert søk'}
                </Typography>
            </Box>

            {/* Advanced Section */}
            <Collapse in={advancedOpen}>
                <Box className={classes.advancedSection}>
                    <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1.5} maxWidth="680px">
                        {/* Row 1 */}
                        {advancedField('Søk på kunde', 'customerNumber', 'F.eks. 500190')}
                        {advancedField('Søk på Ordre', 'orderNumber', 'F.eks. 100001')}

                        {/* Row 2 */}
                        {advancedField('Søk på Rekv.nr.', 'requisitionNumber', 'F.eks. ED23433')}
                        {advancedField('Faktura-/kreditnr.', 'invoiceCreditNumber', 'F.eks. CF82904')}

                        {/* Row 3 - dates */}
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.82rem' }}>
                                    Fra dato
                                </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker
                                    format="DD.MM.YYYY"
                                    value={advancedState.fromDate ? dayjs(advancedState.fromDate) : null}
                                    onChange={(date: Dayjs | null) =>
                                        handleAdvancedChange('fromDate', date ? date.format('YYYY-MM-DD') : '')
                                    }
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            placeholder: 'Velg dato',
                                            className: classes.dateField,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.82rem' }}>
                                    Til dato
                                </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker
                                    format="DD.MM.YYYY"
                                    value={advancedState.toDate ? dayjs(advancedState.toDate) : null}
                                    onChange={(date: Dayjs | null) =>
                                        handleAdvancedChange('toDate', date ? date.format('YYYY-MM-DD') : '')
                                    }
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            placeholder: 'Velg dato',
                                            className: classes.dateField,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>

                    {/* Actions row */}
                    <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
                        <Button variant="outlined" size="medium" onClick={handleClear}
                            sx={{ borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c' } }}>
                            Nullstill
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            disabled={loading}
                            className={classes.searchButton}
                            sx={{ backgroundColor: '#d30535', '&:hover': { backgroundColor: '#b0022c' } }}
                        >
                            Søk
                        </Button>
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    );
}
